/**
 * Workflows API Service
 *
 * HTTP client for workflows module endpoints.
 * Follows established patterns from billing and other modules.
 */

import type { AxiosRequestConfig } from 'axios';

import { api } from '@/lib/api';
import type {
  WorkflowResponse,
  WorkflowListResponse,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowQuery,
  WorkflowExecutionResponse,
  WorkflowExecutionListResponse,
  WorkflowExecutionQuery,
} from '../types';
import type {
  NodeRunInputsResponse,
  NodeRunListResponse,
  WorkflowDefinition,
  WorkflowExportFile,
  WorkflowImportPreview,
  WorkflowImportRequest,
  WorkflowImportResult,
  WorkflowValidationResponse,
} from '@troia-v3/types';

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Cala o `toast.error` do interceptor global (`src/lib/api.ts`) na conferência
 * do desenho: ela roda a cada edição do editor, então com o backend fora cada
 * tecla digitada viraria um aviso vermelho empilhado na tela. Quem chama trata
 * a falha localmente (o painel simplesmente não lista nada). Mesmo padrão de
 * `checklistsApi` e `customersService`.
 */
const CONFERIR_SEM_TOAST: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

/**
 * Workflows API endpoints
 */
export const workflowsApi = {
  // ============================================
  // Workflow CRUD Operations
  // ============================================

  /**
   * Create a new workflow
   */
  async create(data: CreateWorkflowRequest): Promise<WorkflowResponse> {
    const response = await api.post<ApiResponse<WorkflowResponse>>('/workflows', data);
    return response.data.data;
  },

  /**
   * Get workflow by ID
   */
  async getById(id: string): Promise<WorkflowResponse> {
    const response = await api.get<ApiResponse<WorkflowResponse>>(`/workflows/${id}`);
    return response.data.data;
  },

  /**
   * List workflows with pagination and filters
   */
  async list(query?: WorkflowQuery): Promise<WorkflowListResponse> {
    const response = await api.get<ApiResponse<WorkflowListResponse>>('/workflows', {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Update workflow
   */
  async update(id: string, data: UpdateWorkflowRequest): Promise<WorkflowResponse> {
    const response = await api.patch<ApiResponse<WorkflowResponse>>(`/workflows/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete workflow (soft delete)
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/workflows/${id}`);
  },

  // ============================================
  // Workflow Status Operations
  // ============================================

  /**
   * Activate workflow
   */
  async activate(id: string): Promise<WorkflowResponse> {
    const response = await api.post<ApiResponse<WorkflowResponse>>(`/workflows/${id}/activate`);
    return response.data.data;
  },

  /**
   * Pause workflow
   */
  async pause(id: string): Promise<WorkflowResponse> {
    const response = await api.post<ApiResponse<WorkflowResponse>>(`/workflows/${id}/pause`);
    return response.data.data;
  },

  /**
   * Archive workflow
   */
  async archive(id: string): Promise<WorkflowResponse> {
    const response = await api.post<ApiResponse<WorkflowResponse>>(`/workflows/${id}/archive`);
    return response.data.data;
  },

  // ============================================
  // Validação estrutural (2026-08-27)
  // ============================================

  /**
   * Confere a definição sem salvar (`POST /api/workflows/validate`).
   *
   * Desenho quebrado devolve 200 com a lista de problemas em `issues` — não é
   * erro HTTP. Só corpo malformado (Zod) vira 422.
   */
  async validateDefinition(definition: WorkflowDefinition): Promise<WorkflowValidationResponse> {
    const response = await api.post<ApiResponse<WorkflowValidationResponse>>(
      '/workflows/validate',
      { definition },
      CONFERIR_SEM_TOAST,
    );
    return response.data.data;
  },

  // ============================================
  // Workflow Execution Operations
  // ============================================

  /**
   * Trigger manual execution.
   *
   * POST /workflows/trigger { workflowId, triggerData } — o backend executa de
   * verdade via WorkflowRunner e devolve a execução já finalizada. (Antes batia
   * em /workflows/:id/execute, rota inexistente — #192.)
   */
  async triggerManual(id: string, triggerData?: Record<string, any>): Promise<WorkflowExecutionResponse> {
    const response = await api.post<ApiResponse<WorkflowExecutionResponse>>(
      `/workflows/trigger`,
      { workflowId: id, triggerData }
    );
    return response.data.data;
  },

  /**
   * Get execution by ID
   */
  async getExecution(executionId: string): Promise<WorkflowExecutionResponse> {
    const response = await api.get<ApiResponse<WorkflowExecutionResponse>>(
      `/workflows/executions/${executionId}`
    );
    return response.data.data;
  },

  /**
   * List executions for a workflow
   */
  async listExecutions(
    workflowId: string,
    query?: WorkflowExecutionQuery
  ): Promise<WorkflowExecutionListResponse> {
    const response = await api.get<ApiResponse<WorkflowExecutionListResponse>>(
      `/workflows/${workflowId}/executions`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Cancel execution
   */
  async cancelExecution(executionId: string): Promise<WorkflowExecutionResponse> {
    const response = await api.post<ApiResponse<WorkflowExecutionResponse>>(
      `/workflows/executions/${executionId}/cancel`
    );
    return response.data.data;
  },

  // ============================================
  // Contexto do nó (modal "Visualizar contexto")
  // ============================================

  /**
   * Execuções em que ESTE nó recebeu contexto, das mais recentes para as mais
   * antigas. `nodeId` é texto livre do React Flow ('trig', 'wait', 'a') — daí o
   * `encodeURIComponent`.
   */
  async getNodeRuns(
    workflowId: string,
    nodeId: string,
    limit?: number
  ): Promise<NodeRunListResponse> {
    const response = await api.get<ApiResponse<NodeRunListResponse>>(
      `/workflows/${workflowId}/nodes/${encodeURIComponent(nodeId)}/executions`,
      { params: limit !== undefined ? { limit } : undefined }
    );
    return response.data.data;
  },

  /**
   * O que chegou ao nó em cada passagem daquela execução (Repetição pode ter
   * mais de uma).
   */
  async getNodeRunInputs(
    executionId: string,
    nodeId: string
  ): Promise<NodeRunInputsResponse> {
    const response = await api.get<ApiResponse<NodeRunInputsResponse>>(
      `/workflows/executions/${executionId}/nodes/${encodeURIComponent(nodeId)}`
    );
    return response.data.data;
  },

  // ============================================
  // Workflow Count
  // ============================================

  /**
   * Get count of workflows
   */
  async getCount(filters?: { status?: string }): Promise<number> {
    const response = await api.get<ApiResponse<{ count: number }>>('/workflows/count', {
      params: filters,
    });
    return response.data.data.count;
  },

  // ============================================
  // Duplicate Workflow
  // ============================================

  /**
   * Duplicate an existing workflow
   */
  async duplicate(id: string, newName?: string): Promise<WorkflowResponse> {
    const response = await api.post<ApiResponse<WorkflowResponse>>(
      `/workflows/${id}/duplicate`,
      { name: newName }
    );
    return response.data.data;
  },

  // ============================================
  // Trigger Preview Operations
  // ============================================

  /**
   * Preview entities that match a trigger configuration
   * Used to show how many entities would be affected by inactivity or date field triggers
   */
  async previewTriggerEntities(data: {
    triggerType: 'trigger_inactivity' | 'trigger_date_field';
    config: Record<string, unknown>;
    page?: number;
    limit?: number;
  }): Promise<{
    triggerType: string;
    entityType: string;
    total: number;
    entities: Array<{
      id: string;
      name: string;
      subtitle: string;
      status?: string;
    }>;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const response = await api.post<ApiResponse<{
      triggerType: string;
      entityType: string;
      total: number;
      entities: Array<{
        id: string;
        name: string;
        subtitle: string;
        status?: string;
      }>;
      page: number;
      limit: number;
      totalPages: number;
    }>>('/workflows/trigger-preview', data);
    return response.data.data;
  },

  // ============================================
  // Workflow Test Operations
  // ============================================

  /**
   * Test workflow execution with real-time progress via Socket.IO
   * Returns executionId for tracking progress
   *
   * @param id - Workflow ID
   * @param options - Optional test options including pre-generated executionId
   *   - executionId: Pre-generated execution ID (allows frontend to join socket room BEFORE test starts)
   *   - triggerType: Override trigger type for testing
   *   - testContext: Custom context data for test execution
   */
  async testWorkflow(
    id: string,
    options?: {
      executionId?: string;
      triggerType?: string;
      /** Gatilho a simular quando o workflow tem vários (23/09/2026). */
      triggerNodeId?: string;
      testContext?: Record<string, any>;
    }
  ): Promise<{ executionId: string; message: string }> {
    const response = await api.post<ApiResponse<{ executionId: string; message: string }>>(
      `/workflows/${id}/test`,
      options || {}
    );
    return response.data.data;
  },

  // ============================================
  // Import/Export Operations
  // ============================================

  /**
   * Export a single workflow (and any folder it belongs to) as a portable JSON file
   */
  async exportOne(id: string): Promise<WorkflowExportFile> {
    const response = await api.get<ApiResponse<WorkflowExportFile>>(`/workflows/${id}/export`);
    return response.data.data;
  },

  /**
   * Export all workflows (bundle) as a portable JSON file.
   * `usageType` restricts the bundle — the Skills page exports only
   * `usageType: 'skill'` workflows (folders are pruned server-side).
   */
  async exportAll(usageType?: 'automation' | 'skill'): Promise<WorkflowExportFile> {
    const response = await api.get<ApiResponse<WorkflowExportFile>>('/workflows/export', {
      params: usageType ? { usageType } : undefined,
    });
    return response.data.data;
  },

  /**
   * Preview an import: validates the file and resolves cross-references
   * against the current tenant before actually importing anything
   */
  async importPreview(file: WorkflowExportFile): Promise<WorkflowImportPreview> {
    const response = await api.post<ApiResponse<WorkflowImportPreview>>('/workflows/import/preview', file);
    return response.data.data;
  },

  /**
   * Import a previously exported workflow file, applying the reference mappings
   * resolved via importPreview
   */
  async importWorkflow(request: WorkflowImportRequest): Promise<WorkflowImportResult> {
    const response = await api.post<ApiResponse<WorkflowImportResult>>('/workflows/import', request);
    return response.data.data;
  },
};

export default workflowsApi;
